import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { RootState } from 'core';
import mine from 'mime';

import publicContentAPI from '../api.service';
import { refreshDataAfterUpdate } from '../utils';
import { uploadedBulkGraphics } from './graphics.reducer';

import { DeletePublicContentParams } from '../types';
import { IErrors } from 'core/types';
import { PublicContent, PublicContentsParams, PublicContentsType } from 'modules/common/types';

import commonPublicContentAPI from 'modules/common/api.services/public-content.api.service';

export const getGraphics = createAsyncThunk<
  PublicContent,
  Omit<PublicContentsParams, 'contentType'> | undefined,
  { rejectValue: IErrors }
>('manage/graphics', async (payload, thunkApi) => {
  try {
    const response = await commonPublicContentAPI.getPublicContents({
      ...payload,
      contentType: PublicContentsType.Graphic,
    });

    return response.data;
  } catch (err) {
    const error = err as AxiosError<IErrors>;
    if (!error.response) {
      throw err;
    }
    return thunkApi.rejectWithValue(error.response.data);
  }
});

export const createGraphic = createAsyncThunk<
  void,
  PublicContentsParams & { onSuccess: () => void },
  { rejectValue: IErrors; state: RootState }
>('manage/graphics/create', async ({ onSuccess, ...payload }, thunkApi) => {
  try {
    await publicContentAPI.createPublicContent({
      contentType: PublicContentsType.Graphic,
      ...payload,
    });

    const refreshCb = (params: PublicContentsParams) => {
      thunkApi.dispatch(getGraphics(params));
    };

    refreshDataAfterUpdate({
      contentType: PublicContentsType.Graphic,
      store: thunkApi.getState(),
      refreshCb,
    });

    onSuccess();
  } catch (err) {
    const error = err as AxiosError<IErrors>;
    if (!error.response) {
      throw err;
    }
    return thunkApi.rejectWithValue(error.response.data);
  }
});

export const uploadArchiveGraphics = createAsyncThunk<
  void,
  PublicContentsParams,
  { rejectValue: string; state: RootState }
>('manage/graphics/upload/archive', async (payload, thunkApi) => {
  try {
    await publicContentAPI.uploadArchivePublicContents({
      contentType: PublicContentsType.Graphic,
      ...payload,
    });

    const refreshCb = (params: PublicContentsParams) => {
      thunkApi.dispatch(getGraphics(params));
    };

    refreshDataAfterUpdate({
      contentType: PublicContentsType.Graphic,
      store: thunkApi.getState(),
      refreshCb,
    });
  } catch (err) {
    const error = err as AxiosError<string>;
    if (!error.response) {
      throw err;
    }

    return thunkApi.rejectWithValue(error.response.data);
  }
});

export const uploadBulkGraphics = createAsyncThunk<
  void,
  PublicContentsParams[],
  { rejectValue: IErrors; state: RootState }
>('manage/graphics/upload/bulk', async (payload, thunkApi) => {
  try {
    const handleSemaphoreResult = (successfullyUploaded: { name: string; mimeType: string }[]) => {
      const successfullyUploadedFiles = successfullyUploaded.map((item) => {
        const fileExtension = mine.getExtension(item.mimeType);
        return `${item.name}.${fileExtension}`;
      });

      thunkApi.dispatch(uploadedBulkGraphics(successfullyUploadedFiles));

      const refreshCb = (params: PublicContentsParams) => {
        thunkApi.dispatch(getGraphics(params));
      };

      refreshDataAfterUpdate({
        contentType: PublicContentsType.Graphic,
        store: thunkApi.getState(),
        refreshCb,
      });
    };

    await publicContentAPI.uploadBulkPublicContents(payload, handleSemaphoreResult);
  } catch (err) {
    const error = err as AxiosError<IErrors>;
    if (!error.response) {
      throw err;
    }
    return thunkApi.rejectWithValue(error.response.data);
  }
});

export const editGraphic = createAsyncThunk<
  void,
  PublicContentsParams & { onSuccess: () => void },
  { rejectValue: IErrors; state: RootState }
>('manage/graphics/edit', async ({ onSuccess, ...payload }, thunkApi) => {
  try {
    await publicContentAPI.editPublicContent({
      contentType: PublicContentsType.Graphic,
      ...payload,
    });

    const refreshCb = (params: PublicContentsParams) => {
      thunkApi.dispatch(getGraphics(params));
    };

    refreshDataAfterUpdate({
      initialState: false,
      contentType: PublicContentsType.Graphic,
      store: thunkApi.getState(),
      refreshCb,
    });

    onSuccess();
  } catch (err) {
    const error = err as AxiosError<IErrors>;
    if (!error.response) {
      throw err;
    }
    return thunkApi.rejectWithValue(error.response.data);
  }
});

export const editGraphicTags = createAsyncThunk<
  void,
  PublicContentsParams,
  { rejectValue: IErrors; state: RootState }
>('manage/graphics/edit/tags', async (payload, thunkApi) => {
  try {
    await publicContentAPI.editPublicContentTags({
      contentType: PublicContentsType.Graphic,
      ...payload,
    });

    const refreshCb = (params: PublicContentsParams) => {
      thunkApi.dispatch(getGraphics(params));
    };

    refreshDataAfterUpdate({
      initialState: false,
      contentType: PublicContentsType.Graphic,
      store: thunkApi.getState(),
      refreshCb,
    });
  } catch (err) {
    const error = err as AxiosError<IErrors>;
    if (!error.response) {
      throw err;
    }
    return thunkApi.rejectWithValue(error.response.data);
  }
});

export const deleteGraphic = createAsyncThunk<
  void,
  DeletePublicContentParams & { onSuccess: () => void },
  { rejectValue: IErrors; state: RootState }
>('manage/graphics/delete', async (payload, thunkApi) => {
  try {
    await publicContentAPI.deletePublicContent({
      contentType: PublicContentsType.Graphic,
      ...payload,
    });

    const refreshCb = (params: PublicContentsParams) => {
      thunkApi.dispatch(getGraphics(params));
    };

    refreshDataAfterUpdate({
      initialState: false,
      contentType: PublicContentsType.Graphic,
      store: thunkApi.getState(),
      refreshCb,
    });

    payload.onSuccess();
  } catch (err) {
    const error = err as AxiosError<IErrors>;
    if (!error.response) {
      throw err;
    }
    return thunkApi.rejectWithValue(error.response.data);
  }
});

export const deleteBulkGraphics = createAsyncThunk<
  void,
  DeletePublicContentParams & { onSuccess: () => void },
  { rejectValue: IErrors; state: RootState }
>('manage/graphics/delete/bulk', async (payload, thunkApi) => {
  try {
    await publicContentAPI.deleteBulkPublicContents({
      contentType: PublicContentsType.Graphic,
      ...payload,
    });

    const refreshCb = (params: PublicContentsParams) => {
      thunkApi.dispatch(getGraphics(params));
    };

    refreshDataAfterUpdate({
      contentType: PublicContentsType.Graphic,
      store: thunkApi.getState(),
      refreshCb,
    });

    payload.onSuccess();
  } catch (err) {
    const error = err as AxiosError<IErrors>;
    if (!error.response) {
      throw err;
    }
    return thunkApi.rejectWithValue(error.response.data);
  }
});
